/**
 * Zajedničko za stranicu „Partnerske ponude" i karticu klijenta (ADR-071):
 * tipovi iz API ugovora (`docs/plans/PARTNERSKE_PONUDE_PLAN.md` § API ugovor),
 * srpske poruke za kodove grešaka i tabele koje se vide na oba mesta.
 *
 * Upis je samo za sysadmin ulogu `admin` (D1) - server to iznuđuje, a ovde se
 * dugmad za izmenu ni ne prikazuju ostalima.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, Copy, X } from 'lucide-react';
import { loadSession } from '../../auth/auth';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

// ─── Tipovi (API ugovor) ───

export type RewardMode = 'credits' | 'cash';
export type EnrollmentStatus = 'none' | 'pending' | 'rejected' | 'active' | 'inactive';
export type ConversionStatus = 'pending' | 'approved' | 'reversed' | 'paid';
export type PayoutStatus = 'requested' | 'approved' | 'rejected' | 'paid';
/** `postback` = mreža javlja prodaju; `tenant_link` = poseban link za svaki lokal (ADR-078). */
export type OfferKind = 'postback' | 'tenant_link';

/** Valute u kojima mreže javljaju proviziju (ADR-078) - druge server ne prima. */
export const VALUTE = ['RSD', 'EUR', 'USD'] as const;
export type Valuta = typeof VALUTE[number];

export type PostbackMapping = {
  clickId: string;
  txId: string;
  status: string;
  amount?: string | null;
  currency?: string | null;
  occurredAt?: string | null;
  statusMap: Record<string, ConversionStatus>;
};

export type NetworkView = {
  /** `postbackMapping: null` = partner ne šalje potvrde, zarada se unosi ručno. */
  id: string; name: string; slug: string; postbackMapping: PostbackMapping | null; defaultCurrency: string;
  isActive: boolean; notes: string; postbackUrlTemplate: string; createdAt: string; updatedAt: string;
};

export type OfferView = {
  id: string; networkId: string; networkName: string | null; kind: OfferKind; tenantLinkCount?: number | null;
  title: string; customerDescription: string;
  slipText: string; urlTemplate: string; tenantSharePercent: string; estimateRsdPerPurchase: string | null;
  isActive: boolean; sortOrder: number; createdAt: string; updatedAt: string;
};

export type RequestRow = {
  tenantId: string; tenantName: string; taxId: string; isFlatRate: boolean; status: EnrollmentStatus;
  requestedAt: string | null; decidedAt: string | null; reason: string | null; note: string | null;
};

export type ConversionView = {
  networkId: string; networkName: string | null; externalTxId: string; state: ConversionStatus;
  tenantId: string | null; tenantName: string | null; unmatched: boolean; locationId: string | null;
  offerId: string | null; offerTitle: string | null; currency: string | null; amountOriginal: string | null;
  fxRate: string | null; commissionRsd: string | null; tenantSharePercent: string | null; tenantShareRsd: string | null;
  rewardMode: RewardMode | null; needsFxRate: boolean; unsupportedCurrency: boolean;
  /** Zaradu je uneo administrator (link po lokalu) - samo ona sme da se poništi iz panela. */
  manual: boolean; paymentReference: string | null; firstReceivedAt: string;
  events: Array<{ status: ConversionStatus; source: 'postback' | 'admin'; receivedAt: string; occurredAt: string | null }>;
};

export type PayoutView = {
  id: string; amountRsd: string; note: string | null; status: PayoutStatus; createdAt: string;
  decidedAt: string | null; paidAt: string | null; paymentReference: string | null; reason: string | null;
  tenantId?: string; tenantName?: string;
};

export type FxRate = { currency: string; rateDate: string; rateToRsd: string; source: string; enteredBy: string | null; createdAt: string };

export type AffiliateSettings = { cashThresholdRsd: string; publicBaseUrl: string; postbackBaseUrl: string };

// ─── Uloga ───

/** Samo `admin` menja program (D1); podrška i čitalac vide bez dugmadi. */
export const mozeDaMenja = () => loadSession()?.admin.role === 'admin';

// ─── Greške ───

const PORUKE: Record<string, string> = {
  FORBIDDEN: 'Partnerski program menja samo administrator platforme.',
  SLUG_TAKEN: 'Mreža sa tom oznakom već postoji.',
  INVALID_PERCENT: 'Procenat lokala mora biti od 0 do 100.',
  NETWORK_NOT_FOUND: 'Izabrana mreža ne postoji.',
  NOT_FOUND: 'Stavka nije pronađena - osvežite stranu.',
  INVALID_STATE: 'Zahtev više nije u stanju za ovu radnju - osvežite stranu.',
  FX_RATE_EXISTS: 'Kurs za tu valutu i taj dan je već unet i ne menja se.',
  NOT_PENDING: 'Firma više nema zahtev na čekanju - osvežite stranu.',
  ALREADY_ACTIVE: 'Program je već aktivan za ovu firmu.',
  NOT_ACTIVE: 'Program nije aktivan za ovu firmu.',
  FLAT_RATE_NO_CASH: 'Paušalcu se zarada uvek koristi kao umanjenje pretplate - isplata u novcu nije dozvoljena.',
  NETWORK_HAS_NO_POSTBACK: 'Ova mreža ne šalje potvrde - ponuda mora biti „poseban link za svaki lokal".',
  NETWORK_HAS_POSTBACK_OFFERS: 'Mreža ima aktivne ponude koje čekaju potvrde - prvo ih ugasite.',
};

export const URL_GRESKE: Record<string, string> = {
  NOT_URL: 'Adresa nije ispravna.',
  NOT_HTTPS: 'Adresa mora počinjati sa https://.',
  MISSING_CLICK_ID: 'Adresa mora sadržati {click_id}.',
  CLICK_ID_IN_HOST: '{click_id} ne sme biti u adresi servera - samo u putanji ili parametrima.',
};

/** Kod sa servera → srpska poruka; `VALIDATION` već nosi srpsku poruku servera. */
export function porukaGreske(e: any, podrazumevano = 'Radnja nije uspela.'): string {
  const data = e?.data;
  const code: string | undefined = data?.error;
  if (code === 'INVALID_URL_TEMPLATE') return URL_GRESKE[data?.reason] ?? data?.message ?? podrazumevano;
  if (code && code !== 'VALIDATION' && PORUKE[code]) return PORUKE[code];
  return data?.message || (e?.status ? podrazumevano : e?.message) || podrazumevano;
}

/** Ista provera kao `validateUrlTemplate` na serveru, da greška stigne pre slanja. */
export function proveriAdresu(template: string): string | null {
  const t = template.trim();
  if (!t.includes('{click_id}')) return URL_GRESKE.MISSING_CLICK_ID;
  let a: URL;
  let b: URL;
  try {
    a = new URL(t.split('{click_id}').join('aaaa'));
    b = new URL(t.split('{click_id}').join('bbbb'));
  } catch {
    return URL_GRESKE.NOT_URL;
  }
  if (a.protocol !== 'https:') return URL_GRESKE.NOT_HTTPS;
  if (a.host !== b.host || a.username !== b.username || a.password !== b.password) return URL_GRESKE.CLICK_ID_IN_HOST;
  return null;
}

// ─── Prikaz ───

export function rsd(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `${n.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RSD`;
}

export function trenutak(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('sr-RS');
}

export function datum(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('sr-RS');
}

export const NACIN_NAGRADE: Record<RewardMode, string> = { credits: 'Krediti (umanjenje pretplate)', cash: 'Novac (isplata)' };

type Ton = 'ok' | 'info' | 'warn' | 'danger' | 'muted';

export const STATUS_PROGRAMA: Record<EnrollmentStatus, { label: string; ton: Ton }> = {
  none: { label: 'nije uključeno', ton: 'muted' },
  pending: { label: 'čeka odluku', ton: 'warn' },
  rejected: { label: 'odbijeno', ton: 'danger' },
  active: { label: 'aktivno', ton: 'ok' },
  inactive: { label: 'isključeno', ton: 'muted' },
};

export const STATUS_PRODAJE: Record<ConversionStatus, { label: string; ton: Ton }> = {
  pending: { label: 'na čekanju', ton: 'warn' },
  approved: { label: 'odobrena', ton: 'info' },
  paid: { label: 'naplaćeno od mreže', ton: 'ok' },
  reversed: { label: 'poništena', ton: 'danger' },
};

export const STATUS_ISPLATE: Record<PayoutStatus, { label: string; ton: Ton }> = {
  requested: { label: 'zatražena', ton: 'warn' },
  approved: { label: 'odobrena', ton: 'info' },
  rejected: { label: 'odbijena', ton: 'danger' },
  paid: { label: 'isplaćena', ton: 'ok' },
};

const TON: Record<Ton, { bg: string; color: string }> = {
  ok: { bg: 'rgba(34,197,94,0.15)', color: '#86efac' },
  info: { bg: 'rgba(59,130,246,0.18)', color: '#93c5fd' },
  warn: { bg: 'rgba(234,179,8,0.18)', color: '#fde047' },
  danger: { bg: 'rgba(239,68,68,0.15)', color: '#fca5a5' },
  muted: { bg: 'var(--color-bg)', color: 'var(--color-text-muted)' },
};

export function Oznaka({ ton, children, title }: { ton: Ton; children: ReactNode; title?: string }) {
  return (
    <span title={title} style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, whiteSpace: 'nowrap',
      background: TON[ton].bg, color: TON[ton].color,
    }}>
      {children}
    </span>
  );
}

export function KopirajDugme({ tekst, label = 'Kopiraj' }: { tekst: string; label?: string }) {
  const [kopirano, setKopirano] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(tekst);
          setKopirano(true);
          setTimeout(() => setKopirano(false), 2000);
        } catch {
          // Pregledač bez dozvole za međuspremnik - tekst je vidljiv i može se označiti rukom.
        }
      }}
      style={btnSecondary}
    >
      {kopirano ? <Check size={16} /> : <Copy size={16} />} {kopirano ? 'Kopirano' : label}
    </button>
  );
}

export function Dijalog({ naslov, children, onZatvori, sirina = 560 }: { naslov: string; children: ReactNode; onZatvori: () => void; sirina?: number }) {
  useEscapeToClose(onZatvori);
  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...dialog, maxWidth: sirina }}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>{naslov}</h3>
          <button onClick={onZatvori} title="Zatvori" style={btnIcon}><X size={16} /></button>
        </header>
        <div style={{ padding: 20, maxHeight: '75vh', overflow: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

export function Polje({ label, pomoc, children }: { label: string; pomoc?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>{label}</label>
      {children}
      {pomoc && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{pomoc}</div>}
    </div>
  );
}

export function DugmadDijaloga({ onOtkazi, onPotvrdi, potvrdi = 'Sačuvaj', radi, onemoguceno }: {
  onOtkazi: () => void; onPotvrdi: () => void; potvrdi?: string; radi?: boolean; onemoguceno?: boolean;
}) {
  const off = !!radi || !!onemoguceno;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
      <button type="button" onClick={onOtkazi} style={{ ...btnSecondary, width: '100%', justifyContent: 'center' }}>Otkaži</button>
      <button type="button" onClick={onPotvrdi} disabled={off} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: off ? 0.5 : 1 }}>
        {radi ? 'Čuvanje...' : potvrdi}
      </button>
    </div>
  );
}

// ─── Način nagrade (bira se po firmi, ADR-078) ───

/** Dva dugmeta: krediti / novac. Paušalcu je novac zaključan (D9). */
export function IzborNacina({ vrednost, onIzbor, pausalac, onemoguceno }: {
  vrednost: RewardMode | null; onIzbor: (m: RewardMode) => void; pausalac: boolean; onemoguceno?: boolean;
}) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['credits', 'cash'] as RewardMode[]).map((m) => {
          const izabrano = vrednost === m;
          const zabranjeno = m === 'cash' && pausalac;
          const off = !!onemoguceno || zabranjeno;
          return (
            <button
              key={m}
              type="button"
              onClick={() => !izabrano && !off && onIzbor(m)}
              disabled={off}
              title={zabranjeno ? 'Paušalcu se zarada uvek koristi kao umanjenje pretplate.' : undefined}
              data-testid={`affiliate-nacin-${m}`}
              style={{
                ...btnSecondary,
                cursor: off ? 'default' : 'pointer',
                opacity: zabranjeno ? 0.45 : 1,
                background: izabrano ? 'rgba(59,130,246,0.2)' : 'var(--color-bg)',
                borderColor: izabrano ? '#60a5fa' : 'var(--color-border)',
                color: izabrano ? 'var(--color-text-strong)' : 'var(--color-text)',
              }}
            >
              {NACIN_NAGRADE[m]}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
        {pausalac
          ? 'Firma je paušalac: zarada se uvek koristi kao umanjenje pretplate, isplata u novcu nije moguća.'
          : 'Krediti umanjuju račun za pretplatu odmah kad mreža odobri prodaju. Novac lokal podiže tek kad partner plati proviziju Efikasi, iznad praga isplate.'}
      </div>
    </div>
  );
}

// ─── Tabela prodaja (stranica + kartica klijenta) ───

export function ProdajeTabela({ prodaje, izbor, onIzbor, prikaziFirmu = true, onPonisti }: {
  prodaje: ConversionView[];
  /** Ključ `networkId|externalTxId`; bez `onIzbor` nema kolone za izbor. */
  izbor?: Set<string>;
  onIzbor?: (kljuc: string, izabrano: boolean) => void;
  prikaziFirmu?: boolean;
  /** Poništenje ručno unete zarade; prodaje koje je javila mreža poništava samo mreža. */
  onPonisti?: (p: ConversionView) => void;
}) {
  const [otvoreno, setOtvoreno] = useState<string | null>(null);
  if (prodaje.length === 0) return <div style={prazno}>Nema prodaja za izabrane uslove.</div>;
  const kolone = 11 + (onIzbor ? 1 : 0) + (prikaziFirmu ? 1 : 0) + (onPonisti ? 1 : 0);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tabela}>
        <thead>
          <tr>
            {onIzbor && <th style={th} />}
            <th style={th} />
            <th style={th}>Primljeno</th>
            <th style={th}>Mreža</th>
            <th style={th}>Transakcija</th>
            {prikaziFirmu && <th style={th}>Firma</th>}
            <th style={th}>Ponuda</th>
            <th style={{ ...th, textAlign: 'right' }}>Iznos mreže</th>
            <th style={{ ...th, textAlign: 'right' }}>Kurs</th>
            <th style={{ ...th, textAlign: 'right' }}>Provizija</th>
            <th style={{ ...th, textAlign: 'right' }}>Deo lokala</th>
            <th style={th}>Način</th>
            <th style={th}>Stanje</th>
            {onPonisti && <th style={th} />}
          </tr>
        </thead>
        <tbody>
          {prodaje.map((p) => {
            const kljuc = `${p.networkId}|${p.externalTxId}`;
            const otvoren = otvoreno === kljuc;
            return (
              <Fragment key={kljuc}>
                <tr>
                  {onIzbor && (
                    <td style={td}>
                      {/* Naplaćeno od mreže se beleži samo za odobrenu prodaju - server ostale preskače. */}
                      <input
                        type="checkbox"
                        disabled={p.state !== 'approved'}
                        checked={izbor?.has(kljuc) ?? false}
                        onChange={(e) => onIzbor(kljuc, e.target.checked)}
                        title={p.state === 'approved' ? 'Izaberi' : 'Samo odobrena prodaja može da se označi kao naplaćena'}
                      />
                    </td>
                  )}
                  <td style={td}>
                    <button onClick={() => setOtvoreno(otvoren ? null : kljuc)} title="Istorija potvrda" style={btnIcon}>
                      {otvoren ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </td>
                  <td style={td}>{trenutak(p.firstReceivedAt)}</td>
                  <td style={td}>{p.networkName ?? '-'}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12 }}>{p.externalTxId}</td>
                  {prikaziFirmu && (
                    <td style={td}>
                      {p.unmatched
                        ? <Oznaka ton="warn">nesparena</Oznaka>
                        : p.tenantId ? <Link to={`/tenants/${p.tenantId}`}>{p.tenantName ?? p.tenantId}</Link> : '-'}
                    </td>
                  )}
                  <td style={td}>{p.offerTitle ?? '-'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {p.amountOriginal !== null ? `${p.amountOriginal} ${p.currency ?? ''}` : '-'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{p.fxRate ?? '-'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{rsd(p.commissionRsd)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {rsd(p.tenantShareRsd)}
                    {p.tenantSharePercent !== null && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{p.tenantSharePercent} %</div>}
                  </td>
                  <td style={td}>{p.rewardMode ? (p.rewardMode === 'cash' ? 'novac' : 'krediti') : '-'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Oznaka ton={STATUS_PRODAJE[p.state].ton}>{STATUS_PRODAJE[p.state].label}</Oznaka>
                      {p.needsFxRate && <Oznaka ton="danger">treba kurs</Oznaka>}
                      {p.unsupportedCurrency && <Oznaka ton="danger" title="Mreža je javila valutu van RSD/EUR/USD - prodaja se ne pripisuje.">nepodržana valuta</Oznaka>}
                      {p.manual && <Oznaka ton="muted">uneto ručno</Oznaka>}
                    </div>
                  </td>
                  {onPonisti && (
                    <td style={{ ...td, textAlign: 'right' }}>
                      {p.manual && p.state !== 'reversed' && (
                        <button onClick={() => onPonisti(p)} style={btnDangerSecondary}>Poništi</button>
                      )}
                    </td>
                  )}
                </tr>
                {otvoren && (
                  <tr>
                    <td colSpan={kolone} style={{ ...td, background: 'var(--color-bg)' }}>
                      <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4, fontSize: 12 }}>
                        {p.events.map((e, i) => (
                          <li key={i}>
                            <strong>{trenutak(e.receivedAt)}</strong> - {STATUS_PRODAJE[e.status].label}
                            {' · '}{e.source === 'admin' ? 'uneo administrator' : 'potvrda mreže'}
                            {p.manual && p.paymentReference && i === 0 && ` · referenca uplate partnera: ${p.paymentReference}`}
                            {e.occurredAt && ` · nastala ${trenutak(e.occurredAt)}`}
                          </li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tabela isplata (stranica + kartica klijenta) ───

export type IsplataRadnja = { payout: PayoutView; radnja: 'approve' | 'reject' | 'mark_paid' };

export function IsplateTabela({ isplate, prikaziFirmu = true, onRadnja }: {
  isplate: PayoutView[]; prikaziFirmu?: boolean; onRadnja?: (r: IsplataRadnja) => void;
}) {
  if (isplate.length === 0) return <div style={prazno}>Nema zahteva za isplatu.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tabela}>
        <thead>
          <tr>
            <th style={th}>Zatraženo</th>
            {prikaziFirmu && <th style={th}>Firma</th>}
            <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
            <th style={th}>Napomena</th>
            <th style={th}>Stanje</th>
            <th style={th}>Odlučeno</th>
            <th style={th}>Plaćeno / referenca</th>
            <th style={th}>Razlog odbijanja</th>
            {onRadnja && <th style={{ ...th, textAlign: 'right' }}>Radnje</th>}
          </tr>
        </thead>
        <tbody>
          {isplate.map((p) => (
            <tr key={p.id}>
              <td style={td}>{trenutak(p.createdAt)}</td>
              {prikaziFirmu && (
                <td style={td}>{p.tenantId ? <Link to={`/tenants/${p.tenantId}`}>{p.tenantName ?? p.tenantId}</Link> : '-'}</td>
              )}
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{rsd(p.amountRsd)}</td>
              <td style={td}>{p.note || '-'}</td>
              <td style={td}><Oznaka ton={STATUS_ISPLATE[p.status].ton}>{STATUS_ISPLATE[p.status].label}</Oznaka></td>
              <td style={td}>{trenutak(p.decidedAt)}</td>
              <td style={td}>{p.paidAt ? `${trenutak(p.paidAt)} · ${p.paymentReference ?? ''}` : '-'}</td>
              <td style={td}>{p.reason || '-'}</td>
              {onRadnja && (
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    {p.status === 'requested' && (
                      <button onClick={() => onRadnja({ payout: p, radnja: 'approve' })} style={btnSecondary}>Prihvati</button>
                    )}
                    {p.status === 'approved' && (
                      <button onClick={() => onRadnja({ payout: p, radnja: 'mark_paid' })} style={btnSecondary}>Označi plaćeno</button>
                    )}
                    {(p.status === 'requested' || p.status === 'approved') && (
                      <button onClick={() => onRadnja({ payout: p, radnja: 'reject' })} style={btnDangerSecondary}>Odbij</button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Potvrda radnje nad zahtevom za isplatu; odbijanje traži razlog, plaćanje referencu uplate. */
export function IsplataPotvrda({ radnja, onZatvori, onGotovo }: { radnja: IsplataRadnja; onZatvori: () => void; onGotovo: (poruka: string) => void }) {
  const { payout: p } = radnja;
  const iznos = rsd(p.amountRsd);
  const ko = p.tenantName ? ` (${p.tenantName})` : '';
  const izvrsi = async (vrednost?: string) => {
    try {
      if (radnja.radnja === 'approve') {
        await api.post(`/admin/affiliate/payouts/${p.id}/approve`, {});
        onGotovo(`Isplata ${iznos}${ko} je odobrena. Kad novac bude uplaćen, označite je kao plaćenu.`);
      } else if (radnja.radnja === 'reject') {
        await api.post(`/admin/affiliate/payouts/${p.id}/reject`, { reason: (vrednost ?? '').trim() });
        onGotovo(`Isplata ${iznos}${ko} je odbijena; iznos se vraća u raspoloživo.`);
      } else {
        await api.post(`/admin/affiliate/payouts/${p.id}/mark-paid`, { paymentReference: (vrednost ?? '').trim() });
        onGotovo(`Isplata ${iznos}${ko} je označena kao plaćena.`);
      }
    } catch (e) {
      throw new Error(porukaGreske(e));
    }
  };
  if (radnja.radnja === 'approve') {
    return (
      <ConfirmDialog
        title="Odobri isplatu"
        message={`Odobriti isplatu ${iznos}${ko}? Novac se šalje posebno; posle uplate zahtev označite kao plaćen uz referencu.`}
        confirmLabel="Odobri"
        variant="default"
        onConfirm={izvrsi}
        onClose={onZatvori}
      />
    );
  }
  if (radnja.radnja === 'reject') {
    return (
      <ConfirmDialog
        title="Odbij isplatu"
        message={`Odbiti isplatu ${iznos}${ko}? Lokal dobija obaveštenje sa razlogom.`}
        confirmLabel="Odbij"
        variant="danger"
        input={{ label: 'Razlog odbijanja (obavezno)', required: true, multiline: true }}
        onConfirm={izvrsi}
        onClose={onZatvori}
      />
    );
  }
  return (
    <ConfirmDialog
      title="Označi isplatu kao plaćenu"
      message={`Potvrdite da je ${iznos}${ko} uplaćeno lokalu. Upis se ne može poništiti.`}
      confirmLabel="Označi plaćeno"
      variant="default"
      input={{ label: 'Referenca uplate (obavezno)', placeholder: 'npr. broj naloga iz banke', required: true }}
      onConfirm={izvrsi}
      onClose={onZatvori}
    />
  );
}

// ─── Stilovi (design-system § 10: px-4 py-2, gap-1.5, font-medium, ikona 16) ───

export const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 'var(--radius-md, 6px)', background: 'var(--color-primary, #2563eb)', color: '#fff', border: 'none', fontSize: 'var(--font-size-body, 14px)', fontWeight: 500, cursor: 'pointer' };
export const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 'var(--radius-md, 6px)', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 'var(--font-size-meta, 13px)', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' };
export const btnDangerSecondary: React.CSSProperties = { ...btnSecondary, color: '#fca5a5', borderColor: 'rgba(239,68,68,0.4)' };
export const btnIcon: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: 4, background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' };
export const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
export const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' };
export const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16 };
export const tabela: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
export const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
export const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };
export const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 14 };
export const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 14 };
export const warnBox: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(234,179,8,0.15)', color: '#fde047', fontSize: 13, marginBottom: 14, lineHeight: 1.5 };
export const napomena: React.CSSProperties = { padding: 10, borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.5 };
export const prazno: React.CSSProperties = { padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 };
export const mono: React.CSSProperties = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12, overflowWrap: 'anywhere' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', background: 'var(--color-bg-surface)', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' };
